// Package Imports
import React, { useEffect, useState } from 'react';
import { DevtoolsProvider, DevtoolsPanel } from '@refinedev/devtools';
import { notificationProvider } from '@refinedev/antd';
import { Refine } from '@refinedev/core';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import routerBindings, {
  // DocumentTitleHandler,
  UnsavedChangesNotifier,
} from '@refinedev/react-router-v6';
import { BrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfigProvider, Spin } from 'antd';
import { useKeycloak } from '@react-keycloak/web';
// Project Imports
import AppRoutes from 'core/routes/routes';
import ConfigStyle from 'core/constants/ConfigStyle';
import customDataProvider from 'providers/DataProvder';
import { useTokenContext } from 'contexts/resource/ResourceContext';
import DashboardResource from 'core/resources/RulesDashboard';
import {
  REACT_APP_API_URL,
  REACT_APP_API_URL_ORG,
  REACT_APP_API_URL_ROLE_PERM,
  REACT_APP_API_URL_FREQUENCY_MANAGEMENT,
  REACT_APP_API_URL_WORKFLOW_MANAGEMENT,
  REACT_APP_API_URL_UNIFIED,
  REACT_APP_API_URL_PROGRAM,
  REACT_APP_USER_API_URL,
  REACT_APP_API_URL_SIMULATION,
  REACT_APP_API_URL_DASHBOARD_MANAGEMENT,
  REACT_APP_API_URL_TARGET_MANAGEMENT,
  REACT_APP_API_URL_MASTER,
  REACT_APP_API_URL_HOLIDAY,
  REACT_APP_API_URL_COMMUNICATION_MANAGEMENT,
  REACT_APP_API_URL_QC_API,
  REACT_APP_API_URL_CONTEST_MANAGEMENT,
  REACT_APP_API_URL_QUERY_MANAGEMENT,
  REACT_APP_API_BULK_RULE_CREATION,
  REACT_APP_API_URL_GRID_APPROVAL_WORKFLOW,
} from 'config';
import authProvider from 'providers/AuthProvider';
import Loading from 'components/loading/loading';
import { Images } from 'core/common/imagePath';
// type NewDataProvider = DataProvider & {
//   getApiUrl?: () => string;
// };

type I18nProviderType = {
  translate: (key: string, options?: Record<string, unknown>) => string;
  changeLocale: (lang: string) => Promise<void>;
  getLocale: () => string; // Add other methods or properties with their respective types
};

const Initiate = () => {
  const [keycloakReady, setKeycloakReady] = useState(false);
  const { keycloak, initialized } = useKeycloak();
  // const customTitleHandler = () => 'Program Management';

  const { t, i18n } = useTranslation();
  const i18nProvider: I18nProviderType = {
    translate: (key: string, options?: Record<string, unknown>) =>
      t(key, options),
    async changeLocale(lang: string): Promise<void> {
      await i18n.changeLanguage(lang); // Optionally handle any additional logic after changing the language
    },
    getLocale: () => i18n.language,
  };

  const ruleManagementDataProvider = customDataProvider(REACT_APP_API_URL);
  const organizationDataProvider = customDataProvider(REACT_APP_API_URL_ORG);
  const rolePermissionDataProvider = customDataProvider(
    REACT_APP_API_URL_ROLE_PERM
  );
  const frequencyManagementDataProvider = customDataProvider(
    REACT_APP_API_URL_FREQUENCY_MANAGEMENT
  );
  const workflowManagementDataProvider = customDataProvider(
    REACT_APP_API_URL_WORKFLOW_MANAGEMENT
  );
  const unifiedDataProvider = customDataProvider(REACT_APP_API_URL_UNIFIED);
  const programDataProvider = customDataProvider(REACT_APP_API_URL_PROGRAM);
  const userDataProvider = customDataProvider(REACT_APP_USER_API_URL);
  const simulationDataProvider = customDataProvider(
    REACT_APP_API_URL_SIMULATION
  );
  const dashboardManagementDataProvider = customDataProvider(
    REACT_APP_API_URL_DASHBOARD_MANAGEMENT
  );
  const targetManagementDataProvider = customDataProvider(
    REACT_APP_API_URL_TARGET_MANAGEMENT
  );
  const masterSourceDataProvider = customDataProvider(REACT_APP_API_URL_MASTER);
  const holidayManagementDataProvider = customDataProvider(
    REACT_APP_API_URL_HOLIDAY
  );
  const communicationDataProvider = customDataProvider(
    REACT_APP_API_URL_COMMUNICATION_MANAGEMENT
  );
  const bulkruleuploadDataProvider = customDataProvider(
    REACT_APP_API_BULK_RULE_CREATION
  );
  const fetchProductDataProvider = customDataProvider(REACT_APP_API_URL_QC_API);
  const contestDataProvider = customDataProvider(
    REACT_APP_API_URL_CONTEST_MANAGEMENT
  );
  const queryManagementDataProvider = customDataProvider(
    REACT_APP_API_URL_QUERY_MANAGEMENT
  );
  const gridApprovalWorkflowDataProvider = customDataProvider(
    REACT_APP_API_URL_GRID_APPROVAL_WORKFLOW
  );

  const { filteredResources, setToken, setUserSessionId } = useTokenContext();
  const resource = [{ ...DashboardResource }, ...filteredResources];
  useEffect(() => {
    console.log('Keycloak initialization status:', {
      initialized,
      authenticated: keycloak?.authenticated,
    });

    if (initialized) {
      console.log('Keycloak initialized, proceeding...');
      setKeycloakReady(true);
    }
  }, [initialized, keycloak]);

  console.log('Current render state:', {
    initialized,
    keycloakReady,
    keycloak: !!keycloak,
    authenticated: keycloak?.authenticated,
  });

  if (!keycloakReady) {
    console.log('Showing loading state');
    return <Loading />;
  }

  if (!keycloak?.authenticated) {
    console.log('User not authenticated');
    return <h1>User not authenticated</h1>;
  }

  console.log('Rendering main app');
  Spin.setDefaultIndicator(
    <img
      style={{ width: '100px', height: '100px' }}
      src={Images.loaderGif}
      alt="loading"
    />
  );

  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <ConfigProvider csp={{ nonce: 'osian9839980' }} {...ConfigStyle}>
          <DevtoolsProvider>
            <Refine
              dataProvider={{
                default: ruleManagementDataProvider,
                organizationDataProvider,
                rolePermissionDataProvider,
                frequencyManagementDataProvider,
                workflowManagementDataProvider,
                unifiedDataProvider,
                programDataProvider,
                userDataProvider,
                simulationDataProvider,
                dashboardManagementDataProvider,
                masterSourceDataProvider,
                targetManagementDataProvider,
                holidayManagementDataProvider,
                communicationDataProvider,
                fetchProductDataProvider,
                contestDataProvider,
                queryManagementDataProvider,
                bulkruleuploadDataProvider,
                gridApprovalWorkflowDataProvider,
              }}
              i18nProvider={i18nProvider}
              notificationProvider={notificationProvider}
              routerProvider={routerBindings}
              authProvider={authProvider(keycloak, setToken, setUserSessionId)}
              resources={resource}
              options={{
                syncWithLocation: false,
                warnWhenUnsavedChanges: false,
                projectId: 'yFsPTU-0WFGC2-VwzawS',
              }}
            >
              <AppRoutes />
              <RefineKbar />
              <UnsavedChangesNotifier />
              {/* <DocumentTitleHandler handler={customTitleHandler} /> */}
            </Refine>
            <DevtoolsPanel />
          </DevtoolsProvider>
        </ConfigProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
};

export default Initiate;
